import { RegistrationFieldProps } from '../../RegistrationField.types';
import React, {
  ElementRef,
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Breakpoint, Input, Text, useBreakpoint } from '@bonanza-social/moonlight';
import { BottomSheet } from '@bonanza-social/navigation';
import { Platform, Pressable, TextInput } from 'react-native';

type GenderId = 'male' | 'female' | 'other';

const GENDER_OPTIONS: { id: GenderId; text: string }[] = [
  { id: 'male', text: 'Male' },
  { id: 'female', text: 'Female' },
  { id: 'other', text: 'Non Binary' },
];

function GenderPicker(
  {
    onValue,
    value,
    headerNS,
    headerKey,
    placeholderNS,
    placeholderKey,
  }: RegistrationFieldProps<'gender'>,
  _: ForwardedRef<TextInput>,
) {
  const genderRef = useRef<ElementRef<typeof Input>>(null);

  const currentLabel = useMemo(() => {
    return GENDER_OPTIONS.find((o) => o.id === (value as GenderId))?.text ?? '';
  }, [value]);
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === Breakpoint.DESKTOP;
  const snapPoints = useMemo(
    () => (isDesktop ? ['75%'] : ['100%']),
    [isDesktop],
  );
  const maxHeight = useMemo(() => (isDesktop ? '50%' : '70%'), [isDesktop]);

  const [label, setLabel] = useState<string>(currentLabel);
  useEffect(() => setLabel(currentLabel), [currentLabel]);

  const handleSelectValue = useCallback(
    ({ id, text }: { id: GenderId; text: string }) => {
      setLabel(text);
      onValue(id);
    },
    [onValue],
  );

  const onGenderSelectPressIn = useCallback(() => {
    BottomSheet.open({
      namespace: 'common',
      type: 'Select',
      detached: Platform.select({ ios: false, web: true, android: false }),
      snapPoints: snapPoints,
      contentPaddingHorizontal: 'space-0',
      data: {
        selectableOptions: GENDER_OPTIONS,
        headerNS,
        headerKey,
        buttonKey: 'save',
        maxHeight: maxHeight,
        defaultValue: value as string | undefined,
        value: value as string | undefined,
        onSelect: ({ id, text }: { id: GenderId; text: string }) => {
          handleSelectValue({ id, text });
        },
      },
    });
  }, [value, headerNS, headerKey, handleSelectValue, snapPoints, maxHeight]);

  return (
    <Box>
      <Text
        color="darkSurfacePrimary"
        marginBottom="space-300"
        variant="h5"
        locNS="registration"
        locKey="genderLabel"
      />
      <Pressable onPress={onGenderSelectPressIn}>
        <Input
          ref={genderRef}
          value={label}
          placeholderNS={placeholderNS}
          placeholderKey={placeholderKey}
          editable={false}
          onPressIn={onGenderSelectPressIn}
        />
      </Pressable>
    </Box>
  );
}

export default memo(forwardRef(GenderPicker));
